import getConfig from "../config";
import { getRuntimeConfig, type RuntimeConfig } from "../utils/runtimeConfig";

export type GlobalImportContext = "ecm-certificates" | "ecm-customers" | "ecm-vendors";

/**
 * Reads a string from runtime `/config.json` (after load) or Vite `import.meta.env`.
 */
function readConfigEnv(key: keyof RuntimeConfig): string {
    const runtimeConfig = getRuntimeConfig();
    if (runtimeConfig && key in runtimeConfig) {
        const value = runtimeConfig[key];
        return typeof value === "string" ? value : "";
    }
    return (import.meta.env[key as string] as string) || "";
}

/**
 * Shell base URL: One Web Domain uses `AvaAppConfig.hostnames.avaApp`; standalone uses
 * `VITE_AVA_APP_HOST` from runtime config or `.env`. See `getConfig().hostnames.avaApp`.
 */
const resolveImportsBase = (): string => {
    const raw = getConfig().hostnames.avaApp?.trim() ?? "";
    if (!raw) {
        throw new Error(
            "Global Imports requires hostnames.avaApp (AvaAppConfig.hostnames.avaApp)."
        );
    }
    if (raw.startsWith("http://") || raw.startsWith("https://")) {
        return raw.replace(/\/$/, "");
    }
    return `https://${raw.replace(/\/$/, "")}`;
};

/**
 * Full URL of the current EC page (used so AvaApp can send users back after import).
 */
export function getDefaultReturnUrl(): string {
    if (typeof window === "undefined") {
        return "";
    }
    return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

export function getGlobalImportsUrl(
    context: GlobalImportContext,
    returnUrl: string = getDefaultReturnUrl()
): string {
    const url = new URL("/imports", `${resolveImportsBase()}/`);
    url.searchParams.set("context", context);
    if (returnUrl) {
        url.searchParams.set("returnUrl", returnUrl);
    }
    return url.toString();
}

export function assignToGlobalImports(
    context: GlobalImportContext,
    returnUrl: string = getDefaultReturnUrl()
): void {
    window.location.assign(getGlobalImportsUrl(context, returnUrl));
}

/**
 * Use AvaApp Global Imports only when explicitly enabled and the user is authorized.
 *
 * Set `VITE_ENABLE_GLOBAL_IMPORTS` to `"true"` in `/config.json` or `.env` to opt in.
 * Missing, empty, or any other value keeps legacy `/import-*` routes.
 */
export function shouldUseGlobalImportShell(isAuthorizedGlobalImports: boolean): boolean {
    if (readConfigEnv("VITE_ENABLE_GLOBAL_IMPORTS") !== "true") {
        return false;
    }
    if (!isAuthorizedGlobalImports) {
        return false;
    }
    const raw = getConfig().hostnames.avaApp?.trim() ?? "";
    return Boolean(raw);
}
